import { getLocalizedMessage, SupportedLanguage } from "@pimo/pimo-l10n";
import { ExtendedFieldError, GranularPermission } from "product-test-types";

import type { SeatInfo } from "../../helpers/fetch-settings";

type SetValueFn = (path: string, value: unknown) => void;

type SeatUsageInput = {
  seatInfo?: SeatInfo | null;
  clientUsers?: { email?: string | null }[];
  toolUsers?: { email?: string | null; flag?: string }[];
};

// Counts seat usage across all of the customer's tools, matching home-portal's count.
export function computeSeatUsage({
  seatInfo,
  clientUsers,
  toolUsers,
}: SeatUsageInput): {
  seatsUsed: number;
  seatLimit?: number;
  seatsRemaining?: number;
  atCapacity: boolean;
  pendingNewCount: number;
} {
  // Dedup against home-portal's cross-tool member set; clientUsers is a fallback only.
  const authoritativeMembers = seatInfo?.memberEmails;
  const memberSource =
    authoritativeMembers && authoritativeMembers.length > 0
      ? authoritativeMembers
      : (clientUsers ?? []).map((u) => u.email);
  const existingMemberEmails = new Set(
    memberSource.map((email) => email?.trim().toLowerCase()).filter(Boolean)
  );
  const pendingNewEmails = new Set(
    (toolUsers ?? [])
      .filter(
        (u) =>
          u.flag === "create" &&
          u.email?.trim() &&
          !existingMemberEmails.has(u.email.trim().toLowerCase())
      )
      .map((u) => u.email!.trim().toLowerCase())
  );
  const seatsUsed = (seatInfo?.currentUserCount ?? 0) + pendingNewEmails.size;
  const seatLimit = seatInfo?.userLimit;
  const seatsRemaining =
    seatLimit !== undefined ? Math.max(0, seatLimit - seatsUsed) : undefined;
  const atCapacity = seatLimit !== undefined && seatsUsed >= seatLimit;

  return {
    seatsUsed,
    seatLimit,
    seatsRemaining,
    atCapacity,
    pendingNewCount: pendingNewEmails.size,
  };
}

export function generateAccessString(
  permissions: GranularPermission[]
): string {
  return permissions.map((p) => `${p.object}:${p.operation}`).join(";");
}

export function handleRoleChange(options: {
  setValue: SetValueFn;
  formStateValidationErrors?: Record<string, ExtendedFieldError>;
  index: number;
  val: string | string[];
  language: string;
  formState: {
    enableSaveButton?: boolean;
    hasUpdatedUsers?: boolean;
  };
  currentFlag?: string;
}) {
  const {
    setValue,
    formStateValidationErrors,
    index,
    val,
    language,
    formState,
  } = options;

  // normalize val to string (select single)
  const normalizedVal = Array.isArray(val)
    ? (val[0] as string)
    : (val as string);

  setValue(`toolUsers.${index}.permissions.role`, normalizedVal);
  // preserve create/add flags for newly added users
  if (options.currentFlag === "create" || options.currentFlag === "add") {
    setValue(`toolUsers.${index}.flag`, options.currentFlag);
  } else {
    setValue(`toolUsers.${index}.flag`, "update");
  }
  setValue(`enableSaveButton`, true);
  setValue(`hasUpdatedUsers`, true);

  if (val === "admin") {
    setValue(`toolUsers.${index}.permissions.accessLevel`, undefined);
    setValue(`toolUsers.${index}.permissions.projectIds`, undefined);
  } else if (val === "program") {
    setValue(`toolUsers.${index}.permissions.projectIds`, undefined);
  }

  const newErrors = { ...(formStateValidationErrors ?? {}) } as Record<
    string,
    ExtendedFieldError
  >;

  if (!normalizedVal) {
    newErrors[`toolUsers.${index}.permissions.role`] = {
      type: "required",
      message:
        getLocalizedMessage(language, "manageUsers.thisFieldisMandatory") ||
        "This field is required",
      tabContainingErrors: `Manage Access`,
    } as ExtendedFieldError;
  } else {
    delete newErrors[`toolUsers.${index}.permissions.role`];
  }

  if (formState.enableSaveButton !== true) {
    setValue(`enableSaveButton`, true);
  }
  setValue("hasUpdatedUsers", true);
}

export function handleObjectsChange(options: {
  setValue: SetValueFn;
  index: number;
  val: string | string[];
  formState: {
    enableSaveButton?: boolean;
  };
  currentFlag?: string;
}) {
  const { setValue, index, val, formState } = options;
  const selected = Array.isArray(val) ? val : [val];

  const objects: string[] = [];
  const operations: string[] = [];
  if (selected.includes("editProject")) {
    objects.push("project");
    if (!operations.includes("edit")) operations.push("edit");
  }
  if (selected.includes("editItem")) {
    objects.push("item");
    if (!operations.includes("edit")) operations.push("edit");
  }
  if (selected.includes("report")) {
    if (!objects.includes("project")) objects.push("project");
    if (!objects.includes("item")) objects.push("item");
    if (!operations.includes("report")) operations.push("report");
  }

  setValue(`toolUsers.${index}.permissions.object`, objects);
  setValue(`toolUsers.${index}.permissions.operation`, operations);
  if (options.currentFlag === "create" || options.currentFlag === "add") {
    setValue(`toolUsers.${index}.flag`, options.currentFlag);
  } else {
    setValue(`toolUsers.${index}.flag`, "update");
  }
  if (formState.enableSaveButton !== true) {
    setValue(`enableSaveButton`, true);
  }
  setValue("hasUpdatedUsers", true);
}

export function handleProjectIdsChange(options: {
  setValue: SetValueFn;
  index: number;
  value: string | string[];
  formState: {
    enableSaveButton?: boolean;
  };
  currentFlag?: string;
}) {
  const { setValue, index, value, formState } = options;
  const v = Array.isArray(value) ? value : [value];
  setValue(`toolUsers.${index}.permissions.projectIds`, v);
  if (options.currentFlag === "create" || options.currentFlag === "add") {
    setValue(`toolUsers.${index}.flag`, options.currentFlag);
  } else {
    setValue(`toolUsers.${index}.flag`, "update");
  }
  if (formState.enableSaveButton !== true) {
    setValue(`enableSaveButton`, true);
  }
  setValue("hasUpdatedUsers", true);
}

export function getAccessOptions(
  role: string,
  projectName: string,
  itemName: string,
  language: SupportedLanguage
) {
  if (role === "program") {
    return [
      {
        name: generateAccessString([
          { object: "project", operation: "read" },
          { object: "item", operation: "read" },
        ]),
        label: getLocalizedMessage(
          language,
          "manageUsers.permissions.program.viewOnly"
        ),
      },
      {
        name: generateAccessString([
          { object: "project", operation: "read,edit" },
          { object: "item", operation: "read,edit" },
        ]),
        label: getLocalizedMessage(
          language,
          "manageUsers.permissions.program.viewAndManage",
          { itemName: itemName, projectName: projectName }
        ),
      },
      {
        name: generateAccessString([
          { object: "project", operation: "read,report" },
          { object: "item", operation: "read,report" },
        ]),
        label: getLocalizedMessage(
          language,
          "manageUsers.permissions.program.viewAndReport",
          { itemName: itemName, projectName: projectName }
        ),
      },
      {
        name: generateAccessString([
          { object: "project", operation: "read,edit,report" },
          { object: "item", operation: "read,edit,report" },
        ]),
        label: getLocalizedMessage(
          language,
          "manageUsers.permissions.program.viewManageAndReport",
          { itemName: itemName, projectName: projectName }
        ),
      },
    ];
  }
  if (role === "project") {
    return [
      {
        name: generateAccessString([
          { object: "project", operation: "read" },
          { object: "item", operation: "read" },
        ]),
        label: getLocalizedMessage(
          language,
          "manageUsers.permissions.project.viewOnly"
        ),
      },
      {
        name: generateAccessString([
          { object: "project", operation: "read" },
          { object: "item", operation: "read,edit" },
        ]),
        label: getLocalizedMessage(
          language,
          "manageUsers.permissions.project.viewAndManage",
          { itemName: itemName }
        ),
      },
      {
        name: generateAccessString([
          { object: "project", operation: "read,report" },
          { object: "item", operation: "read,report" },
        ]),
        label: getLocalizedMessage(
          language,
          "manageUsers.permissions.project.viewAndReport",
          { itemName: itemName }
        ),
      },
      {
        name: generateAccessString([
          { object: "project", operation: "read,report" },
          { object: "item", operation: "read,edit,report" },
        ]),
        label: getLocalizedMessage(
          language,
          "manageUsers.permissions.project.viewManageAndReport",
          { itemName: itemName }
        ),
      },
    ];
  }
  return [];
}

export function handleAccessChange(options: {
  setValue: SetValueFn;
  index: number;
  val: string;
  formState: {
    enableSaveButton?: boolean;
  };
  currentFlag?: string;
}) {
  const { setValue, index, val, formState } = options;

  const parts = val.split(";");
  const granularPermissions = parts.map((part) => {
    const [obj, ops] = part.split(":");
    return {
      object: obj,
      operation: ops,
    };
  });

  setValue(
    `toolUsers.${index}.permissions.granularPermissions`,
    granularPermissions
  );
  setValue(`toolUsers.${index}.permissions.granularPermissionsString`, val);
  setValue(`toolUsers.${index}.permissions.operation`, undefined);
  setValue(`toolUsers.${index}.permissions.object`, undefined);

  if (options.currentFlag === "create" || options.currentFlag === "add") {
    setValue(`toolUsers.${index}.flag`, options.currentFlag);
  } else {
    setValue(`toolUsers.${index}.flag`, "update");
  }
  if (formState.enableSaveButton !== true) {
    setValue(`enableSaveButton`, true);
  }
  setValue("hasUpdatedUsers", true);
}
